import {
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleSlash,
  Clock,
  Eye,
  Palmtree,
  Sun,
} from "lucide-react";
import {
  ATTENDANCE_STATUS_CONFIG,
  classifyIndianHoliday,
  formatMonthYearIndian,
  isTodayInMonth,
} from "../../Moduels/attendance/indianCalendarUtils";
import { isCalendarWeekOffDay, isWeekOffWeekdayName } from "../../Moduels/attendance/attendanceCalendarUtils";
import "../../styles/IndianCalendar.css";

const STATUS_ICONS = {
  P: CheckCircle2,
  A: CircleSlash,
  HD: Clock,
  L: Palmtree,
  WO: Calendar,
  H: Sun,
};

function cellToneClass(_weekdayName, holiday, statusCode, isWeekOff) {
  if (statusCode) {
    const tone = ATTENDANCE_STATUS_CONFIG[statusCode]?.tone;
    if (tone === "present") return "ind-cal-cell--present";
    if (tone === "absent") return "ind-cal-cell--absent";
    if (tone === "halfday") return "ind-cal-cell--halfday";
    if (tone === "leave") return "ind-cal-cell--leave";
    if (tone === "weekoff") return "ind-cal-cell--weekoff";
    if (tone === "holiday") return "ind-cal-cell--holiday-status";
  }
  if (holiday) return "ind-cal-cell--holiday";
  if (isWeekOff) return "ind-cal-cell--weekoff";
  return "bg-white";
}

export function CalendarLegend({ compact = false }) {
  const items = [
    { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Present" },
    { cls: "bg-red-100 text-red-800 border-red-200", label: "Absent (LOP)" },
    { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Half Day" },
    { cls: "bg-blue-100 text-blue-800 border-blue-200", label: "Leave" },
    { cls: "bg-orange-100 text-orange-800 border-orange-200", label: "Comp Off" },
    { cls: "bg-violet-100 text-violet-800 border-violet-200", label: "Holiday / Gazetted" },
    { cls: "bg-indigo-100 text-indigo-800 border-indigo-200", label: "Week Off" },
  ];
  return (
    <div className={`flex flex-wrap gap-2 ${compact ? "" : "mt-4"}`}>
      {items.map((it) => (
        <span key={it.label} className={`ind-legend-pill border ${it.cls}`}>
          {it.label}
        </span>
      ))}
    </div>
  );
}

export default function IndianMonthCalendar({
  monthKey,
  cells,
  weekdayHeaders,
  holidayMap = {},
  dayStatusMap = {},
  dayLeaveTypeMap = {},
  compOffDayMap = {},
  weekOffWeekdays = null,
  monthlyOff = null,
  fixedWorkingDays = null,
  onDayClick,
  highlightDay = null,
  busy = false,
  readOnly = false,
  savingDay = null,
  showLegend = true,
  isDayUnavailable,
  onMonthChange,
}) {
  const monthMeta = formatMonthYearIndian(monthKey);

  return (
    <div className="ind-cal-grid-wrap">
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-slate-50 to-teal-50/40">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-600 text-white shadow-sm">
            <Calendar className="w-5 h-5" />
          </div>
          <div className="flex items-center gap-2">
            {onMonthChange ? (
              <button
                type="button"
                onClick={() => onMonthChange(-1)}
                className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition shadow-sm"
                aria-label="Previous month"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            ) : null}
            <p className="text-lg font-bold text-slate-900">{monthMeta.label}</p>
            {onMonthChange ? (
              <button
                type="button"
                onClick={() => onMonthChange(1)}
                className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition shadow-sm"
                aria-label="Next month"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {readOnly ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-[11px] font-semibold text-slate-700 mr-1">
              <Eye className="w-3.5 h-3.5" />
              View only
            </span>
          ) : null}
          <span className="ind-legend-pill border bg-emerald-100 text-emerald-800 border-emerald-200">Present</span>
          <span className="ind-legend-pill border bg-red-100 text-red-800 border-red-200">Absent (LOP)</span>
          <span className="ind-legend-pill border bg-amber-100 text-amber-800 border-amber-200">Half Day</span>
          <span className="ind-legend-pill border bg-blue-100 text-blue-800 border-blue-200">Leave</span>
          <span className="ind-legend-pill border bg-violet-100 text-violet-800 border-violet-200 opacity-75">Holiday</span>
          <span className="ind-legend-pill border bg-indigo-100 text-indigo-800 border-indigo-200 opacity-75">Week Off</span>
        </div>
      </div>

      <div className="p-3 md:p-4">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {weekdayHeaders.map((name) => (
            <div
              key={name}
              className={`ind-cal-weekday ${
                isWeekOffWeekdayName(name, weekOffWeekdays)
                  ? name === "Sunday"
                    ? "sun"
                    : "sat"
                  : "weekday"
              }`}
            >
              <span className="hidden sm:inline">{name}</span>
              <span className="sm:hidden">{name.slice(0, 3)}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell) => {
            if (cell.empty) {
              return <div key={cell.key} className="ind-cal-empty min-h-[5.5rem]" aria-hidden />;
            }

            const { day, weekdayName } = cell;
            const statusCode = dayStatusMap[day] || dayStatusMap[String(day)] || "";
            const isWeekOff = isCalendarWeekOffDay(day, cell.weekdayIndexMon0, monthKey, {
              weekOffWeekdays,
              monthlyOff,
              fixedWorkingDays,
            });
            const holiday = holidayMap[day];
            const isToday = isTodayInMonth(monthKey, day);
            const isSelected = highlightDay != null && Number(highlightDay) === day;
            const holidayClass = holiday
              ? classifyIndianHoliday(`${monthKey}-${String(day).padStart(2, "0")}`, holiday.name)
              : null;
            const saving = savingDay === day;
            const unavailable = Boolean(isDayUnavailable?.(day));
            const isHolidayLocked = statusCode === "H";
            const editable = Boolean(onDayClick) && !readOnly && !busy && !unavailable && !isHolidayLocked;
            const clickable = Boolean(onDayClick) && !readOnly && !busy && !unavailable;
            const markedLeave = ["L", "PL", "UL"].includes(statusCode) ? dayLeaveTypeMap[day] : null;
            const compOffLeave = compOffDayMap[day] === "used" || String(markedLeave?.code || "").toUpperCase() === "CO";
            const shownStatus = compOffLeave ? "L" : statusCode;
            const shownCfg = shownStatus ? ATTENDANCE_STATUS_CONFIG[shownStatus] : null;
            const ShownIcon = shownStatus ? STATUS_ICONS[shownStatus] : null;
            const leaveType = compOffLeave ? { code: "CO", name: "Comp Off Leave" } : (shownStatus === "L" ? markedLeave : null);

            return (
              <div
                key={cell.key}
                role={clickable ? "button" : "gridcell"}
                tabIndex={clickable ? 0 : -1}
                onClick={() => clickable && onDayClick?.(day)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && clickable) {
                    e.preventDefault();
                    onDayClick?.(day);
                  }
                }}
                className={`ind-cal-cell ${editable ? "cursor-pointer" : isHolidayLocked && clickable ? "ind-cal-cell--org-level" : "ind-cal-cell--readonly"} ${cellToneClass(
                  weekdayName,
                  holiday,
                  shownStatus,
                  isWeekOff && !shownStatus
                )} ${isToday ? "ind-cal-cell--today" : ""} ${
                  isSelected ? "ind-cal-cell--selected" : ""
                } ${
                  busy && !readOnly ? "ind-cal-cell--disabled" : ""
                } ${unavailable ? "ind-cal-cell--disabled opacity-50" : ""}`}
                title={
                  unavailable
                    ? "Before joining date — attendance is not applicable"
                    : readOnly
                      ? shownStatus
                        ? `${shownCfg?.label || shownStatus} — view only`
                        : holiday
                          ? `${holiday.name} — view only`
                          : "View only — attendance locked for this month"
                      : isHolidayLocked
                        ? holiday
                          ? `${holiday.name}${holiday.is_optional ? " (Restricted)" : " (Gazetted)"} — organization-level, click for details`
                          : "Holiday — organization-level, click for details"
                        : shownStatus === "WO"
                          ? "Week off — click to mark Present, Absent, Half Day, or Leave"
                          : compOffLeave
                            ? "Comp Off Leave"
                          : "Click to change attendance"
                }
              >
                <div className="ind-cal-cell-top flex items-start justify-between gap-1">
                  <span className="ind-cal-cell-wd text-[9px] font-semibold text-slate-400 uppercase tracking-wide">
                    {weekdayName.slice(0, 3)}
                  </span>
                  {holidayClass?.category === "gazetted" ? (
                    <span className="ind-cal-national-badge">Govt</span>
                  ) : null}
                </div>

                <div className="ind-cal-day-num mt-0.5">{day}</div>

                {holiday ? (
                  <div className="mt-1 space-y-0.5">
                    <div className="ind-cal-holiday-chip" title={holiday.name}>
                      {holiday.name}
                    </div>
                    {holiday.is_optional ? (
                      <span className="text-[9px] text-amber-700 font-medium">Restricted</span>
                    ) : (
                      <span className="text-[9px] text-violet-700 font-medium">Gazetted</span>
                    )}
                  </div>
                ) : null}

                <div className="mt-auto pt-1 flex flex-col items-center justify-center gap-1 min-h-[2.25rem]">
                  {shownCfg && ShownIcon ? (
                    <div
                      className="ind-cal-status-pill w-full flex flex-col items-center justify-center gap-0.5 py-1 rounded-lg border shadow-sm"
                      data-tone={compOffLeave ? "leave" : shownCfg.tone}
                    >
                      <ShownIcon className="w-4 h-4" />
                      <span className="text-sm font-extrabold tracking-wide">
                        {compOffLeave ? "CO" : shownCfg.short}
                        {saving ? "…" : ""}
                      </span>
                      {compOffLeave ? (
                        <span className="text-[9px] font-bold text-blue-700">Comp Off Leave</span>
                      ) : shownStatus === "L" && leaveType ? (
                        <>
                          <span className="text-[9px] font-bold text-blue-600">
                            {String(leaveType.code).toUpperCase() === "CO" ? "Comp Off Leave" : leaveType.code}
                          </span>
                          {String(leaveType.code).toUpperCase() !== "CO" ? (
                            <span className="text-[8px] font-medium opacity-70 leading-tight text-center truncate w-full">{leaveType.name}</span>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-[9px] font-medium opacity-80">{shownCfg.label}</span>
                      )}
                    </div>
                  ) : (
                    <span className={`text-[10px] font-medium ${readOnly || unavailable ? "text-slate-400" : "text-teal-600"}`}>
                      {unavailable
                        ? "Before joining"
                        : holiday
                          ? "Holiday"
                          : readOnly
                            ? "—"
                            : saving
                              ? "Saving…"
                              : "Tap to mark"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend is now in the calendar header */}
      </div>
    </div>
  );
}
